import React from 'react';
import {
    lowerCase,
    upperFirst,
    orderBy,
    groupBy,
    map,
    forIn,
    find
} from 'lodash';
import { getUTCDateString } from 'helpers/dateHelper';
import { formatDateTime, TIME_ZONE } from 'helpers/dateTimeFormatter';
import { IconDataLogger, IconComment, IconClose } from 'svgIcons/MotionPortalIcons';
import { dateKind, EventTypesIconWithName, eventStatus, causeOfEvents } from '../constants';
import { eventTypeOrderSmSe, pageSize } from './constants';


const getSSColumns = (translate, closeEventHandler, setSelectedEvent, selectedDateKind) => {
    return [
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogNameTitle'),
            dataIndex: 'messageText',
            fixed: 'left',
            width: 200,
            isSorting: true,
            onCell: (event) => ({
                onClick: () => setSelectedEvent(event)
            })
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogAdviceTitle'),
            dataIndex: 'adviceMessage',
            width: 200,
            isSorting: true,
            onCell: (event) => ({
                onClick: () => setSelectedEvent(event)
            }),
            render: (adviceMessage) => <div>{adviceMessage || '-'}</div>
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogActionsTitle'),
            dataIndex: 'rawEvent',
            width: 100,
            // eslint-disable-next-line react/display-name
            render: (event) => <div>{event?.statusCode !== eventStatus.CLOSED ?
                <div className='close-button'>
                    <button onClick={() => { closeEventHandler(event); }}>
                        <IconClose />
                    </button>
                </div> : null}
            </div>
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogOccurredOnTitle'),
            dataIndex: 'timestamp',
            width: 200,
            isSorting: true,
            onCell: (event) => ({
                onClick: () => setSelectedEvent(event)
            }),
            render: (occurredOn) => formatDateTime(occurredOn, { timeZone: selectedDateKind === dateKind.UTC ? TIME_ZONE.UTC : TIME_ZONE.LOCAL })
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogStatusTitle'),
            dataIndex: 'statusCode',
            width: 150,
            onCell: (event) => ({
                onClick: () => setSelectedEvent(event)
            })
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogClosingReasonTitle'),
            dataIndex: 'closingReasonCode',
            width: 150,
            onCell: (event) => ({
                onClick: () => setSelectedEvent(event)
            })
        }
    ];
};

const getDriveColumns = (translate, setSelectedEvent, assistanceCapability, selectedDateKind, nameWithType = false) => {
    const eventNameColumn = {
        title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogNameTitle'),
        dataIndex: 'messageText',
        fixed: 'left',
        isSorting: true,
        onCell: (event) => ({
            onClick: () => assistanceCapability && setSelectedEvent(event)
        })
    };

    if (nameWithType) {
        eventNameColumn.dataIndex = 'rawEvent';
        eventNameColumn.render = (event) => <div className='event-name-with-type'>
            <div className='event-type-icon'>{EventTypesIconWithName.find(et => et.typeID === event.severityCode)?.icon}</div>
            <div>{event.messageText}</div>
        </div>;
    }

    return [
        eventNameColumn,
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogCauseTitle'), //*
            dataIndex: 'cause',
            width: 200,
            isSorting: true,
            onCell: (data) => ({
                onClick: () => assistanceCapability && setSelectedEvent(data)
            }),
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogTimeTitle'), //*
            dataIndex: 'timestamp',
            width: 200,
            isSorting: true,
            onCell: (data) => ({
                onClick: () => assistanceCapability && setSelectedEvent(data)
            }),
            render: (time) => formatDateTime(time, { timeStyle: 'long', timeZone: selectedDateKind === dateKind.UTC ? TIME_ZONE.UTC : TIME_ZONE.LOCAL })
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogDeviceTimestampTitle'), //*
            dataIndex: 'deviceTimestamp',
            width: 200,
            isSorting: true,
            onCell: (data) => ({
                onClick: () => assistanceCapability && setSelectedEvent(data)
            }),
            render: (deviceTime) => formatDateTime(deviceTime, { timeStyle: 'withMS', timeZone: selectedDateKind === dateKind.UTC ? TIME_ZONE.UTC : TIME_ZONE.LOCAL })
        },

        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogAssetName'), //*
            dataIndex: 'assetName',
            width: 200,
            isSorting: true,
            onCell: (data) => ({
                onClick: () => assistanceCapability && setSelectedEvent(data)
            }),
        },
        {
            title: translate('ABB.Powertrain.Frontend.detailedInfoEventLogInfoTitle'),
            dataIndex: 'info',
            width: 100,
            // eslint-disable-next-line react/display-name
            render: (info) => <div className='other-info'>
                {info.hasDataLoggers || info.hasBinaryLogger ? <div className='action-icon'><IconDataLogger /></div> : null}
                {info.hasComments ? <div className='action-icon'><IconComment /></div> : null}
            </div>,
            onCell: (data) => ({
                onClick: () => assistanceCapability && setSelectedEvent(data)
            })
        }
    ];
};

const getAssetNameById = (assets, id) => {
    const assetById = find(assets, (asset) => {
        return asset.id === id;
    });
    return assetById?.label;
};

const mapEvents = (assets, translate, events = []) => {
    return map(events, (event) => {
        return {
            adviceMessage: event.adviceMessage,
            eventId: event.eventId,
            rawEvent: {
                ...event,
                sortingItem: event.messageText
            },
            assetName: getAssetNameById(assets, event.assetId),
            messageText: event.messageText,
            description: event.description,
            occurredOn: event.timestamp,
            timestamp: event.timestamp,
            statusCode: event.statusCode ? translate(`ABB.Powertrain.Frontend.eventListFilterSSEventStatus${event.statusCode}`) : '',
            closingReasonCode: event.closingReasonCode ? translate(`ABB.Powertrain.Frontend.eventClosingReason${event.closingReasonCode}`) : '',
            cause: event.causeCode ? translate(`ABB.Powertrain.Frontend.eventCauseOption${event.causeCode}`) : '',
            deviceTimestamp: event.deviceTimestamp,
            time: event.timestamp,
            info: {
                hasComments: event.hasComments,
                hasDataLoggers: event.hasDataLoggers,
                hasBinaryLogger: event.hasBinaryLogger
            }
        };
    });
};

const formatDriveEventLogGroups = (eventLogs, assets, translate) => {
    // Deduplicate by eventId first to prevent duplicate events
    const uniqueEvents = [];
    const seenEventIds = new Set();

    (eventLogs || []).forEach(event => {
        if (event?.eventId && !seenEventIds.has(event.eventId)) {
            seenEventIds.add(event.eventId);
            uniqueEvents.push(event);
        }
    });

    // Group by both messageText and severityCode for logic
    const groupedEvents = groupBy(uniqueEvents, event => {
        const messageText = event?.messageText || 'Unknown';
        const severityCode = event?.severityCode || 'Unknown';
        return `${messageText}_${severityCode}`;
    });
    const formattedData = [];
    const seenGroupNames = new Set();

    forIn(groupedEvents, (eventGroup, key) => {
        if (!eventGroup || eventGroup.length === 0) { return; }

        // Extract messageText from the composite key
        const messageText = key.split('_')[0];
        const groupName = `${messageText} (${eventGroup.length})`;

        // Prevent duplicate group names
        if (seenGroupNames.has(groupName)) {
            return;
        }
        seenGroupNames.add(groupName);

        formattedData.push({
            id: `eventGroup_${key}`,
            eventGroupName: groupName,
            eventType: eventGroup[0]?.severityCode,
            events: mapEvents(assets, translate, eventGroup)
        });
    });

    return formattedData.sort((a, b) => {
        const getPriority = (key) => {
            if (key?.startsWith('-')) { return 1; } // '-' has the highest priority
            if (key?.startsWith('+')) { return 2; } // '+' comes after '-'
            if (key?.match(/^\d/)) { return 3; } // Numbers come after special characters
            if (key?.match(/^[a-zA-Z]/)) { return 4; } // Letters come last
            return 5; // Empty key or Any other characters
        };

        const priorityA = getPriority(a.eventGroupName);
        const priorityB = getPriority(b.eventGroupName);

        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }

        // Case-insensitive alphabetical sort with numeric awareness
        return a.eventGroupName.localeCompare(b.eventGroupName, undefined, {
            numeric: true,
            sensitivity: 'base'
        });
    });
};

const getEventTypeLabel = (key) => `ABB.Powertrain.Frontend.lblEventListEventType${upperFirst(lowerCase(key))}`;

const formatSmartSensorEventLogGroups = (eventLogs, assets, translate) => {
    const groupedEvents = groupBy(eventLogs, 'severityCode');
    const formattedData = [];
    forIn(groupedEvents, (eventGroup, key) => {
        formattedData.push({
            eventGroupName: `${translate(getEventTypeLabel(key))} (${eventGroup.length})`,
            eventType: eventGroup[0].severityCode,
            events: mapEvents(assets, translate, eventGroup),
            orderID: eventGroup[0]?.severityCode && eventTypeOrderSmSe[eventGroup[0].severityCode]
        });
    });

    return orderBy(formattedData, 'orderID');
};

const formatEventLogList = (eventLogs, assets, translate) => {
    return {
        events: mapEvents(assets, translate, eventLogs),
    };
};

export const formatEventLogs = (eventLogs, assets, isSS, grouped, translate) => {
    if (isSS) {
        return formatSmartSensorEventLogGroups(eventLogs, assets, translate);
    }
    else if (grouped) {
        return formatDriveEventLogGroups(eventLogs, assets, translate);
    }
    return formatEventLogList(eventLogs, assets, translate);
};


export const getColumnConfig = (isSS, grouped, translate, closeEventHandler, setSelectedEvent, assistanceCapability, dateKind) => {
    return isSS ?
        getSSColumns(translate, closeEventHandler, setSelectedEvent, dateKind) :
        getDriveColumns(translate, setSelectedEvent, assistanceCapability, dateKind, !grouped);
};

export const getDateInterval = (dateSelection, selectedDateKind) => {
    if (selectedDateKind === dateKind.UTC) {
        const from = new Date(dateSelection.from);
        const offsetInHours = from.getTimezoneOffset();
        const to = new Date(dateSelection.to);
        dateSelection = {
            from: getUTCDateString(new Date(from.getTime() - offsetInHours * 60 * 1000)),
            to: getUTCDateString(new Date(to.getTime() - offsetInHours * 60 * 1000))
        };
    }
    return dateSelection;
};


export const formatEventListRequest = (assets, dateSelection, filters) => {
    return {
        assetIds: assets.length > 0
            ? map(assets.filter((asset) => asset.id !== null), (asset) => asset.id)
            : [], // Filter out assets with null or undefined ids
        timestampFrom: dateSelection.from,
        timestampTo: dateSelection.to,
        pageSize,
        ...filters?.eventTypes ? { severityCodes: filters?.eventTypes } : {},
        ...filters?.eventStatus && filters?.eventStatus !== eventStatus.ALL ? { statusCodes: [filters.eventStatus] } : {},
        ...filters?.causeOfEvent && filters?.causeOfEvent !== causeOfEvents.All ? { causeCodes: [filters.causeOfEvent] } : {},
        ...filters.withCommentsOnly ? { hasComments: filters.withCommentsOnly } : {},
        ...filters.withDataLoggersOnly ? { hasDataLoggers: filters.withDataLoggersOnly } : {},
        ...filters.withBinaryLoggerOnly ? { hasBinaryLogger: filters.withBinaryLoggerOnly } : {},
        messageTextContains: filters.eventName,
        externalEventIds: []
    };
};
